// Read-only merchandising: never changes variants, prices, inventory or cart data.
(() => {
  if (customElements.get('mm-discount-badge')) return;
  const cache = new Map();
  const query = `query BundleSaving($id: ID!, $country: CountryCode!, $after: String) @inContext(country: $country) {
    node(id: $id) { ... on ProductVariant {
      id price { amount currencyCode }
      components(first: 100, after: $after) {
        nodes { quantity productVariant { id price { amount currencyCode } } }
        pageInfo { hasNextPage endCursor }
      }
    } }
  }`;
  const cents = money => {
    const amount = Number(money?.amount);
    return money?.amount != null && String(money.amount).trim() !== '' && Number.isFinite(amount) && amount >= 0 ? Math.round(amount * 100) : NaN;
  };
  const percentage = (original, price) => {
    if (!Number.isFinite(original) || !Number.isFinite(price) || original <= 0 || price < 0 || price >= original) return 0;
    return Math.round(((original - price) / original) * 100);
  };
  customElements.define('mm-discount-badge', class extends HTMLElement {
    connectedCallback() {
      this.pending = false;
      this.controller = new AbortController();
      this.unsubscribe = subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, ({ data }) => {
        if (!this.closest('product-info')?.contains(data.event.target)) return;
        this.pending = true;
        this.hidden = true;
        this.controller.abort();
      });
      this.calculate();
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.unsubscribe?.();
    }
    async components() {
      const id = `gid://shopify/ProductVariant/${this.dataset.variantId}`;
      const key = `${id}:${this.dataset.country}:${this.dataset.currency}`;
      const cached = cache.get(key);
      if (cached && Date.now() - cached.time < 60000) return cached.value;
      let after = null;
      let total = 0;
      let count = 0;
      let price;
      const cursors = new Set();
      do {
        const response = await fetch('/api/2026-07/graphql.json', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ query, variables: { id, country: this.dataset.country, after } }),
          signal: this.controller.signal
        });
        if (!response.ok) throw new Error('Bundle data unavailable');
        const result = await response.json();
        const variant = result.data?.node;
        if (result.errors?.length || variant?.id !== id || variant.price?.currencyCode !== this.dataset.currency) throw new Error('Incomplete bundle data');
        const variantPrice = cents(variant.price);
        if (!Number.isFinite(variantPrice) || (price !== undefined && price !== variantPrice)) throw new Error('Inconsistent bundle data');
        price = variantPrice;
        const components = variant.components;
        if (!Array.isArray(components?.nodes) || !components.pageInfo) throw new Error('Missing components');
        for (const component of components.nodes) {
          const money = component.productVariant?.price;
          const componentPrice = cents(money);
          if (!component.productVariant?.id || money?.currencyCode !== this.dataset.currency || !Number.isFinite(componentPrice) || !Number.isInteger(component.quantity) || component.quantity <= 0) throw new Error('Invalid component');
          total += componentPrice * component.quantity;
          count++;
        }
        if (!components.pageInfo.hasNextPage) break;
        after = components.pageInfo.endCursor;
        if (!after || cursors.has(after)) throw new Error('Incomplete component pagination');
        cursors.add(after);
      } while (true);
      const value = { total, price, count };
      cache.set(key, { time: Date.now(), value });
      return value;
    }
    async calculate() {
      this.hidden = true;
      const price = this.dataset.price === '' ? NaN : Number(this.dataset.price);
      if (!this.dataset.variantId || !Number.isFinite(price) || price < 0) return;
      // Timeout applies only to the optional presentation, never to product selection.
      const timeout = setTimeout(() => this.controller.abort(), 8000);
      try {
        const bundle = await this.components();
        if (!this.isConnected || this.pending || bundle.price !== price) return;
        if (bundle.count > 0) {
          // Exact selected component variants and quantities; never compare-at pricing.
          this.dataset.presentation = 'original';
          if (!Number.isFinite(bundle.total) || bundle.total <= 0 || bundle.total <= price) return;
          const amount = new Intl.NumberFormat(document.documentElement.lang || 'en', {
            style: 'currency', currency: this.dataset.currency, currencyDisplay: 'narrowSymbol',
            minimumFractionDigits: 2, maximumFractionDigits: 2
          }).format(bundle.total / 100);
          const original = document.createElement('s');
          original.textContent = amount;
          this.replaceChildren(original);
          this.setAttribute('aria-label', `Original standalone total: ${amount}`);
          this.hidden = false;
        } else {
          // Individual products retain their existing compare-at percentage badge.
          const discount = percentage(Number(this.dataset.compareAt), price);
          if (discount > 0) {
            this.textContent = `${discount}% OFF`;
            this.hidden = false;
          }
        }
      } catch (_) {
        // Unknown component prices cannot establish a genuine standalone saving.
      } finally {
        clearTimeout(timeout);
      }
    }
  });
})();
